if (!customElements.get('product-video')) {
  customElements.define(
    "product-video",
    class ProductVideo extends HTMLElement {
      static getVideoIdPromise = null;
      static youtubeApiPromise = null;

      static loadScriptAsync(uri, id) {
        return new Promise((resolve, reject) => {
          if (document.getElementById(id)) {
            resolve();
            return;
          }
          const script = document.createElement("script");
          script.type = "text/javascript";
          script.src = uri;
          script.async = true;
          script.id = id;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error(`Failed to load ${uri}`));
          document.body.append(script);
        });
      }

      static loadGetVideoId() {
        if (window.getVideoId) return Promise.resolve();
        if (!ProductVideo.getVideoIdPromise) {
          ProductVideo.getVideoIdPromise = ProductVideo.loadScriptAsync(
            "https://cdn.jsdelivr.net/npm/get-video-id/dist/get-video-id.umd.min.js",
            "getVideoId"
          );
        }
        return ProductVideo.getVideoIdPromise;
      }

      static loadYoutubeApi() {
        if (window.YT && window.YT.Player) return Promise.resolve();
        if (!ProductVideo.youtubeApiPromise) {
          ProductVideo.youtubeApiPromise = new Promise((resolve) => {
            window.onYouTubeIframeAPIReady = resolve;
            ProductVideo.loadScriptAsync("https://www.youtube.com/iframe_api", "youtube-iframe-api");
          });
        }
        return ProductVideo.youtubeApiPromise;
      }

      connectedCallback() {
        this.videoMount = this.querySelector(".js-video-mount");
        this.videoWrapper = this.querySelector(".js-video-wrapper");
        this.loadButton = this.querySelector(".js-video-load-button");
        this.videoType = this.getAttribute("data-video-type");
        this.videoUrl = this.getAttribute("data-video-url");
        this.nativeVideo = null;
        this.ytPlayer = null;

        if (!this.loadButton || !this.videoUrl) return;

        this.loadButton.addEventListener("click", (e) => {
          e.preventDefault();

          if (this.videoType === "native") {
            if (this.nativeVideo) {
              this.replayNativeVideo();
            } else {
              this.loadButton.classList.add("is-animating");
              this.loadNativeVideo();
            }
          } else {
            if (this.ytPlayer) {
              this.replayYoutubeVideo();
            } else {
              this.loadButton.classList.add("is-animating");
              this.loadYoutubeVideo();
            }
          }
        });
      }

      // Native, self-hosted video - nothing is fetched until the play button is clicked.
      loadNativeVideo() {
        const video = document.createElement("video");
        video.setAttribute("playsinline", "");
        video.setAttribute("controls", "");
        video.className = "video-section__native-video";

        const source = document.createElement("source");
        source.src = this.videoUrl;
        source.type = "video/mp4";
        video.appendChild(source);

        video.addEventListener("playing", () => {
          this.loadButton.classList.remove("is-animating");
        });

        // Stay on the player when the video ends (native controls let the
        // customer replay it) instead of reverting to the poster + play
        // button - that overlay should only reappear on a fresh page load.
        this.videoMount.appendChild(video);
        this.nativeVideo = video;
        video.load();

        // Reveal the (previously display:none) player container first - the
        // video element must have layout before the browser will fetch it.
        this.classList.add("is-loaded");
        this.videoWrapper.classList.remove("is-hidden");
        video.play().catch(() => {});
      }

      // Video (and, for YouTube, the player) already loaded once - just restart playback,
      // no re-fetching of scripts or the video source.
      replayNativeVideo() {
        this.classList.add("is-loaded");
        this.videoWrapper.classList.remove("is-hidden");
        this.nativeVideo.currentTime = 0;
        this.nativeVideo.play().catch(() => {});
      }

      replayYoutubeVideo() {
        this.classList.add("is-loaded");
        this.videoWrapper.classList.remove("is-hidden");
        this.ytPlayer.seekTo(0);
        this.ytPlayer.playVideo();
      }

      // YouTube fallback - scripts and player are only requested once the play button is clicked.
      async loadYoutubeVideo() {
        try {
          await ProductVideo.loadGetVideoId();

          const videoId = getVideoId(this.videoUrl).id;
          const videoService = getVideoId(this.videoUrl).service;

          if (videoService !== "youtube") {
            this.classList.add("is-hidden");
            this.loadButton.classList.remove("is-animating");
            console.error("video url is malformed, or the video service is not supported");
            return;
          }

          await ProductVideo.loadYoutubeApi();

          this.ytPlayer = new window.YT.Player(this.videoMount, {
            height: "390",
            width: "640",
            playerVars: {
              playsinline: 1,
              rel: 0,
              enablejsapi: 1,
              modestbranding: 1,
            },
            events: {
              onStateChange: this.onPlayerStateChange,
              onReady: (event) => {
                event.target.cueVideoById(videoId);
                const readyInterval = setInterval(() => {
                  if (event.target.getPlayerState() === 5) {
                    clearInterval(readyInterval);
                    event.target.playVideo();
                  }
                }, 500);
              },
            },
          });
        } catch (error) {
          this.classList.add("is-hidden");
          this.loadButton.classList.remove("is-animating");
          console.error(error);
        }
      }

      onPlayerStateChange(event) {
        const thisNode = event.target.getIframe().parentNode.parentNode;
        const videoWrapper = thisNode.querySelector(".js-video-wrapper");
        const loadButton = thisNode.querySelector(".js-video-load-button");

        if (event.data === YT.PlayerState.CUED) {
          thisNode.classList.add("is-loaded");
          videoWrapper.classList.remove("is-hidden");
          videoWrapper.classList.remove("is-playing");
          loadButton.classList.remove("is-animating");
        }
        if (event.data === YT.PlayerState.PLAYING) {
          videoWrapper.classList.add("is-playing");
        }
        if (event.data === YT.PlayerState.ENDED) {
          // Stay on the player (YouTube shows its own replay UI) instead of
          // reverting to the poster + play button overlay.
          videoWrapper.classList.remove("is-playing");
        }
      }

      disconnectedCallback() {}
    }
  );
}
